import React from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { Link } from 'react-router-dom'
import HeaderSix from '../components/header/HeaderSix'
import FooterSix from '../components/footer/FooterSix'
import ScrollAnimation from '../components/motion/ScrollAnimation'

function MedicalBillingService() {
    const cards = [
        {
            num: '01',
            title: 'Eligibility Verification',
            text: 'We confirm coverage before every single appointment. Not after the service is rendered. Not when the denial comes back three weeks later. Before. The most avoidable category of denials is eliminated before a single claim leaves the building.',
        },
        {
            num: '02',
            title: 'Medical Coding',
            text: 'Certified coders who know your specialty, not generalists guessing at complexity levels. One consistent under-coding error in a busy practice is worth thousands in lost revenue every month. We document correctly, code accurately, and capture everything you earn.',
        },
        {
            num: '03',
            title: 'Claims Submission',
            text: 'Every claim is scrubbed before it goes out. Every error is caught before it becomes a rejection. Submitted within 24 to 48 hours, not when someone gets around to it.',
        },
        {
            num: '04',
            title: 'Denial Management',
            text: 'We treat every denial as a recoverable claim until proven otherwise. Root cause analyzed. Documentation corrected. Appeal prepared and submitted. 40 to 60% of denied claims are paid as revenue when someone actually fights for them.',
        },
        {
            num: '05',
            title: 'Accounts Receivable Follow-Up',
            text: 'Aging AR is not a natural state. It’s a symptom of a process that stops short. We follow up at defined intervals, persistently and systematically — until every outstanding claim is resolved with a clear outcome.',
        },
        {
            num: '06',
            title: 'Patient Billing',
            text: "Professional and empathetic patient communication that collects what’s owed without damaging the relationship your clinical team spent years building. Patient collection rates improve significantly when the process is consistent and the communication is handled with care.",
        },
        {
            num: '07',
            title: 'Monthly Reporting',
            text: 'Denial rates. Collection totals. AR aging. Clean claim rates. Payer performance. Every month, in plain language, so you always know exactly where your revenue stands and what’s being done about it.',
        },
    ]

    const specialties = [
        {
            title: 'Primary Care & Family Medicine',
            text: 'Preventive visit coding, chronic care management, Medicare wellness visits, same-day modifier application, high-volume processing without sacrificing accuracy.',
        },
        {
            title: 'Mental Health & Behavioral Health',
            text: 'Time-based psychotherapy coding, medical necessity documentation, crisis billing, substance abuse treatment, authorization management, the specialty where most billing companies quietly struggle and we genuinely excel.',
        },
        {
            title: 'Cardiology',
            text: 'Authorization management for high-dollar procedures, diagnostic coding, technical versus professional component billing and payer-specific bundling rules that change without warning.',
        },
        {
            title: 'Urgent Care',
            text: "Speed without errors. Workers’ compensation coordination, real-time eligibility verification, appropriate evaluation and management level selection at volume.",
        },
        {
            title: 'Specialty Medicine',
            text: 'Dermatology, endocrinology, gastroenterology, orthopedics, neurology, OB/GYN, each with its own coding universe, authorization requirements, and payer quirks, we already understand.',
        },
        {
            title: 'Multi-Specialty Groups',
            text: 'One dedicated team managing the full complexity of multiple specialties under one roof — coordinated, compliant, and consistent across every service line.',
        },
    ]

    const faqs = [
        {
            q: 'What is revenue cycle management in healthcare?',
            a: 'Revenue cycle management covers the entire financial process from patient scheduling to final payment, including eligibility verification, medical coding, claims submission, denial management, payment posting, and patient collections.',
        },
        {
            q: 'Why do healthcare providers outsource medical billing?',
            a: 'Outsourcing reduces administrative costs, significantly lowers denial rates, accelerates payment timelines, and provides access to specialty-specific expertise that most practices cannot maintain cost-effectively with in-house staff.',
        },
        {
            q: 'What denial rate should I expect from an outsourced billing company?',
            a: 'A well-run outsourced billing operation should achieve denial rates of 5 to 8 percent. If your current denial rate is above 10 percent, you are losing significant recoverable revenue every single month.',
        },
        {
            q: 'How long does it take to onboard with a medical billing company?',
            a: 'Most practices are fully operational with an outsourced billing team within 2 to 4 weeks, including integration with existing practice management and electronic health record systems.',
        },
    ]

    return (
        <div className="index-six service-page service-page--medical-billing">
            <HeaderSix />
            <main>
                <ScrollAnimation direction="fade" delay={0.03} duration={0.38}>
                    <div className="about-page-breadcrumb">
                        <div className="container">
                            <h1 className="about-page-breadcrumb__title">Medical Billing Services</h1>
                        </div>
                    </div>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.5}>
                    <section className="td-section rts-section-gap td-service-hero">
                        <div className="container">
                            <div className="td-service-hero__panel">
                                <div className="row align-items-center g-4 g-lg-5">
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__kicker">Medical Billing Services</div>
                                        <h2 className="td-service-hero__title">
                                            Your Claims Are Going Out. Not Enough Is Coming Back.
                                        </h2>
                                        <p className="td-service-hero__lead">
                                            Specialized medical billing and revenue cycle management for US healthcare
                                            providers across every specialty. We don’t just submit claims, we make sure they
                                            get paid.
                                        </p>
                                        <div className="button-area">
                                            <Link to="/contactus" className="rts-btn btn-primary">
                                                Schedule Your Free Consultation
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__visual">
                                            <img
                                                src="/assets/images/about/industries.jpeg"
                                                alt="Service overview"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="left" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">
                                            You Delivered the Care. You Deserve to Get Paid for It.
                                        </h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <p className="disc">
                                        Here’s what’s actually happening inside most practices right now.
                                    </p>
                                    <p className="disc">
                                        Claims are going out late because the billing team is overwhelmed. Codes are being
                                        selected conservatively because nobody has time to document the complexity. Denials
                                        are piling up in a queue that nobody is systematically working through. And somewhere
                                        in that pile is money that belongs to you, sitting uncollected, not because the
                                        insurance company won’t pay it, but because nobody followed up with the right
                                        documentation at the right time.
                                    </p>
                                    <p className="disc">
                                        The average in-house billing team runs denial rates between 15 and 25 percent. Our
                                        clients average 5 to 8. That difference in practice, doing half a million dollars
                                        annually, is real money. Earned money. Yours.
                                    </p>
                                    <p className="disc">
                                        Outsourcing your revenue cycle to TechDigit typically reduces billing overhead by 40
                                        to 60 percent while recovering revenue that was already earned and already owed.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head text-center mb-5">
                                        <p className="td-section-label">What We Handle</p>
                                        <h2 className="td-section-title">The Entire Revenue Cycle. Nothing Left to Chance.</h2>
                                        <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                            <div className="row g-4">
                                {cards.map((c) => (
                                    <div key={c.num} className="col-lg-6">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">{c.num}</span>
                                                        <h4 className="pillar-title">{c.title}</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">{c.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="right" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">We Don’t Adapt to Your Specialty. We Already Know It.</h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <div className="row g-4 mt-1">
                                        {specialties.map((s) => (
                                            <div key={s.title} className="col-md-6">
                                                <div className="service-quad-card h-100">
                                                    <h3 className="service-quad-card__title">{s.title}</h3>
                                                    <p className="service-quad-card__text">{s.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="fade" delay={0.04} duration={0.42}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">What Practices Actually Experience</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <p className="disc mt-3">
                                        A family medicine practice reduced denials from 18 percent to under 6 percent in four
                                        months. An urgent care center recovered money from claims a previous company had
                                        written off entirely. A cardiology group cut authorization denials nearly in half within
                                        seven months.
                                    </p>
                                    <p className="disc">
                                        These aren’t exceptional outcomes. They’re what happens when billing is handled by
                                        people who specialize in nothing else.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="faq-techdigit td-section td-section--faq rts-faq-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="faq-techdigit-inner">
                                        <div className="td-section-head text-center mb-5">
                                            <p className="td-section-label">FAQ</p>
                                            <h2 className="td-section-title">Questions People Ask</h2>
                                            <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                        </div>
                                        <div className="faq-accordion-area">
                                            <Accordion defaultActiveKey="0">
                                                {faqs.map((faq, i) => (
                                                    <Accordion.Item key={i} eventKey={String(i)}>
                                                        <Accordion.Header>{faq.q}</Accordion.Header>
                                                        <Accordion.Body>{faq.a}</Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="down" delay={0.05} duration={0.44}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">Let’s Find Out Exactly What Your Billing Is Costing You</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <p className="disc mt-3">
                                        Not a vague estimate. A real conversation about your current denial rate, your AR aging,
                                        your collection gaps and what fixing them would actually mean for your practice’s
                                        revenue.
                                    </p>
                                    <p className="disc">
                                        The first consultation is free and there is no obligation.
                                    </p>
                                    <div className="button-area mt-4">
                                        <Link to="/contactus" className="rts-btn btn-primary">
                                            Schedule Your Free Consultation
                                        </Link>
                                    </div>
                                    <p className="disc mt-3">
                                        info@techdigitsolutions.com
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>
            </main>
            <FooterSix />
        </div>
    )
}

export default MedicalBillingService

